import { computed, ref, unref, watch } from 'vue';

const IMAGE_EXTENSIONS = ['png', 'svg'];

const uniq = items => [...new Set(items.filter(Boolean))];

const buildNameVariants = name => {
  const value = String(name || '');
  return uniq([value, value.replace(/-/g, '_'), value.replace(/_/g, '-')]);
};

const toDarkFileName = fileName => fileName.replace(/(\.[^.]+)$/, '-dark$1');

const toAssetPath = fileName => `/dashboard/images/integrations/${fileName}`;

const buildLogoCandidates = (id, explicitLogo, { dark = false } = {}) => {
  const candidates = [];
  const add = fileName => {
    if (fileName) candidates.push(toAssetPath(fileName));
  };

  if (explicitLogo) {
    add(dark ? toDarkFileName(explicitLogo) : explicitLogo);
  }

  buildNameVariants(id).forEach(name => {
    IMAGE_EXTENSIONS.forEach(ext => {
      add(`${dark ? `${name}-dark` : name}.${ext}`);
    });
  });

  if (dark && explicitLogo) {
    add(explicitLogo);
  }

  return uniq(candidates);
};

const useCandidatePath = candidates => {
  const index = ref(0);

  watch(
    candidates,
    () => {
      index.value = 0;
    },
    { immediate: true }
  );

  const currentPath = computed(
    () => candidates.value[index.value] || candidates.value[0] || ''
  );

  const moveToNextPath = () => {
    if (index.value < candidates.value.length - 1) {
      index.value += 1;
    }
  };

  return { currentPath, moveToNextPath };
};

export const useIntegrationLogo = (id, explicitLogo) => {
  const logoCandidates = computed(() =>
    buildLogoCandidates(unref(id), unref(explicitLogo))
  );
  const darkLogoCandidates = computed(() =>
    buildLogoCandidates(unref(id), unref(explicitLogo), { dark: true })
  );

  const { currentPath: logoPath, moveToNextPath: handleLogoError } =
    useCandidatePath(logoCandidates);
  const { currentPath: darkLogoPath, moveToNextPath: handleDarkLogoError } =
    useCandidatePath(darkLogoCandidates);

  return {
    logoPath,
    darkLogoPath,
    handleLogoError,
    handleDarkLogoError,
  };
};
